import { Component } from 'react'
import { createRoot } from 'react-dom/client'
import * as THREE from 'three'
import { Canvas } from '@react-three/fiber'
import './styles.css'
import App from './App'

function getPrecision(context) {
  const supports = (precision) => {
    const vertex = context.getShaderPrecisionFormat(context.VERTEX_SHADER, precision)
    const fragment = context.getShaderPrecisionFormat(context.FRAGMENT_SHADER, precision)

    return Boolean(vertex?.precision && fragment?.precision)
  }

  if (supports(context.HIGH_FLOAT)) return 'highp'
  if (supports(context.MEDIUM_FLOAT)) return 'mediump'

  throw new Error('The browser did not expose a usable shader precision.')
}

function createRenderer(canvas) {
  const options = {
    alpha: true,
    antialias: false
  }
  const context = canvas.getContext('webgl2', options) || canvas.getContext('webgl', options)

  if (!context) {
    throw new Error('The browser could not create a WebGL context.')
  }

  const isWebGL2 = typeof WebGL2RenderingContext !== 'undefined' && context instanceof WebGL2RenderingContext
  const hasFloatTextures = isWebGL2 || Boolean(context.getExtension('OES_texture_float'))
  const hasFloatTargets = Boolean(context.getExtension(isWebGL2 ? 'EXT_color_buffer_float' : 'WEBGL_color_buffer_float'))
  const hasVertexTextures = context.getParameter(context.MAX_VERTEX_TEXTURE_IMAGE_UNITS) > 0

  if (!hasFloatTextures || !hasFloatTargets || !hasVertexTextures) {
    context.getExtension('WEBGL_lose_context')?.loseContext()
    throw new Error('This browser does not support the floating-point textures used by the particle effect.')
  }

  try {
    return new THREE.WebGLRenderer({
      canvas,
      context,
      ...options,
      precision: getPrecision(context)
    })
  } catch (error) {
    context.getExtension('WEBGL_lose_context')?.loseContext()
    throw error
  }
}

function WebGLFallback() {
  return (
    <main className="webgl-fallback" role="status">
      <p>Interactive background unavailable</p>
      <span>Enable hardware acceleration or try a browser with WebGL2 support.</span>
    </main>
  )
}

class WebGLErrorBoundary extends Component {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  render() {
    return this.state.failed ? <WebGLFallback /> : this.props.children
  }
}

const appRoot = createRoot(document.getElementById('root'))

appRoot.render(
  <WebGLErrorBoundary>
    <Canvas
      camera={{ fov: 25, position: [0, 0, 4.5] }}
      dpr={[1, 1.5]}
      gl={createRenderer}
      linear
    >
      <App />
    </Canvas>
  </WebGLErrorBoundary>
)

if (module.hot) {
  module.hot.dispose(() => {
    appRoot.unmount()
  })
}
